import { Model } from './model.js';

export class MultiModelManager {
    constructor(scene, environment, materialManager, settings) {
        this.scene = scene;
        this.environment = environment;
        this.materialManager = materialManager;
        this.settings = settings;
        this.models = [];
    }    // Charge les modèles à partir de la configuration
    async loadModels(modelsConfig) {
        //console.log('🚀 MultiModelManager.loadModels appelé avec', modelsConfig?.length, 'modèles');
        
        // Vérification que la configuration est un array valide
        if (!Array.isArray(modelsConfig)) {
            console.warn('Configuration de modèles invalide, doit être un array');
            return;
        }

        // Nettoyer les anciens modèles avant de charger les nouveaux
        this.clearModels();

        // Filtrer les modèles selon les paramètres de détection activés
        const filteredModels = modelsConfig.filter((config, index) => {
            // Vérifier que l'URL du modèle est présente
            if (!config.modelUrl) {
                console.warn(`Modèle ${index} ignoré : URL manquante`);
                return false;
            }

       

            return true;
        });

        // Créer les modèles et collecter les promesses de chargement
        const modelLoadPromises = filteredModels.map((config, index) => {
            // Créer une nouvelle instance du modèle avec la configuration
            //console.log(`🔨 Création du modèle ${index}: ${config.modelName}`);
           
            const model = new Model(
                this.scene,
                this.environment,
                this.materialManager,
                config, // Configuration complète du modèle
                config.startAnimationName,
                config.options?.wiggleEnabled ?? true,
                this.settings
            );

            // Ajouter des métadonnées pour le tracking et l'identification
            model.config = config;
            model.index = index;
            this.models.push(model);

            // Retourner la promesse de chargement du modèle
            return new Promise((resolve) => {
                const originalOnModelLoaded = model.onModelLoaded;

                model.onModelLoaded = (loadedModel) => {
                    // Appeler l'ancien callback s'il existait
                    if (originalOnModelLoaded) {
                        originalOnModelLoaded(loadedModel);
                    }

                    // Appliquer les remplacements de matériaux spécifiques à ce modèle
                    if (this.materialManager && config.materialReplacements) {
                       
                        this.materialManager.applyMaterialReplacements(loadedModel, config);
                    }

                   // //console.log(`✅ Modèle "${config.modelName || 'sans nom'}" chargé`);
                    resolve(model);
                };
            });
        });

        // Attendre que tous les modèles soient chargés
        try {
            window.loader.setMessage('Loading models...');
            await Promise.all(modelLoadPromises);

            // Log informatif sur les modèles chargés
            const faceModels = this.models.filter(m => m.config?.modelType === 'face').length;
            const poseModels = this.models.filter(m => m.config?.modelType === 'pose').length;
            const otherModels = this.models.filter(m => !['face', 'pose'].includes(m.config?.modelType)).length;

           // //console.log(`🎉 Tous les modèles chargés: ${faceModels} face, ${poseModels} pose, ${otherModels} autres (Total: ${this.models.length})`);
            window.loader.setMessage('Modèles chargés');


            // Déclencher l'événement de fin de chargement
            if (this.onAllModelsLoaded) {
                this.onAllModelsLoaded();
            }

        } catch (error) {
            console.error('Erreur lors du chargement des modèles:', error);
        }
    }// Met à jour tous les modèles avec les données de tracking
    updateModels(coordsResults, deltaTime) {
        // Appliquer les coordonnées de tracking à tous les modèles
        this.models.forEach(model => {
            // Vérifier si le type de détection correspondant est activé
            const modelType = model.config?.modelType;
            if ((modelType === 'face' && !this.settings.faceDetect) ||
                (modelType === 'pose' && !this.settings.poseDetect)) {
                // Ne pas mettre à jour les modèles dont le type de détection est désactivé
                return;
            }

            model.updateWithTracking(coordsResults, deltaTime);
        });
    }    
    
    clearModels() {
        //console.log('🧹 Nettoyage des modèles précédents:', this.models.length);
        this.models.forEach(model => {
            // Nettoyer les ressources du modèle
            if (model.dispose) {
                model.dispose();
            }
            // Retirer le modèle de la scène Three.js s'il existe
            if (model.model) {
                this.scene.remove(model.model);
            }
        });
        // Vider le tableau des modèles
        this.models = [];
        //console.log('✅ Nettoyage terminé');
    }

    // Vérifie la présence de modèles de type "pose"
    hasPoseModels() {
        return this.models.some(model => model.config?.modelType === 'pose');
    }

    // Active ou désactive le wiggle pour tous les modèles
    setWiggleEnabled(enabled) {
        this.models.forEach(model => {
            model.setWiggleEnabled(enabled);
        });
    }

    // Retourne le nombre total de modèles chargés
    getModelCount() {
        return this.models.length;
    }

    // Récupère un modèle par son nom
    getModelByName(modelName) {
        return this.models.find(model => model.config?.modelName === modelName);
    }

    // Récupère un sous-objet par son nom dans tous les modèles
    getSubObjectByName(subObjectName) {
        for (const model of this.models) {
            if (model.subObjectInstances) {
                const subObject = model.subObjectInstances.find(sub => sub.config?.objectName === subObjectName);
                if (subObject) {
                    return {
                        model: model,
                        subObject: subObject.object,
                        config: subObject.config
                    };
                }
            }
        }
        return null;
    }    // Récupère tous les modèles d'un type spécifique (face, pose)
    getModelsByType(modelType) {
        return this.models.filter(model => model.config?.modelType === modelType);
    }

    // Vérifie si un type de modèle devrait être actif selon les paramètres de détection
    isModelTypeEnabled(modelType) {
        switch (modelType) {
            case 'face':
                return this.settings.faceDetect;
            case 'pose':
                return this.settings.poseDetect;
            default:
                return true; // Les autres types de modèles sont toujours actifs
        }
    }

    // Récupère seulement les modèles actifs (dont le type de détection est activé)
    getActiveModels() {
        return this.models.filter(model =>
            this.isModelTypeEnabled(model.config?.modelType)
        );
    }
}
